import { Capacitor } from "@capacitor/core";

import { getResolvedTheme, subscribeTheme } from "@/contexts/theme";

/** True when running inside the iOS/Android Capacitor shell. */
export function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Native-only chrome setup, called fire-and-forget from main.tsx:
 * - status bar style follows the resolved theme (and live theme changes),
 * - keyboard resizes the webview natively so the chat composer stays visible,
 * - hides the branded splash screen once the web app has booted.
 * All plugin imports are dynamic so the web build never even loads them.
 */
export async function initNativeChrome(): Promise<void> {
  if (!isNative()) return;
  try {
    const [statusBarModule, keyboardModule, splashModule] = await Promise.all([
      import("@capacitor/status-bar"),
      import("@capacitor/keyboard"),
      import("@capacitor/splash-screen"),
    ]);

    const { StatusBar, Style } = statusBarModule;
    const syncStatusBar = () => {
      void StatusBar.setStyle({
        style: getResolvedTheme() === "dark" ? Style.Dark : Style.Light,
      }).catch(() => undefined);
    };
    syncStatusBar();
    subscribeTheme(syncStatusBar);

    const { Keyboard, KeyboardResize } = keyboardModule;
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(
      () => undefined,
    );

    await splashModule.SplashScreen.hide().catch(() => undefined);
  } catch {
    // Plugins unavailable (plain web) — nothing to do.
  }
}
